"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _utils = require("../utils");
var _validations = require("../validations");
var _analyticsUtils = require("../utils/analyticsUtils");
var _constants = require("../constants");
var _staticContent = require("../staticContent");
var _errorHandlerUtility = _interopRequireDefault(require("../utils/errorHandlerUtility"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var isRichTextField = field => field.type === _constants.FIELD_TYPES.RICH_TEXT_FIELD;
var isFieldGroup = field => field.type === _constants.FIELD_TYPES.FIELD_GROUP_FIELD;
var isStepTracker = field => field.type === _constants.FIELD_TYPES.STEP_TRACKER_FIELD;
var fileName = 'useGetForm.jsx';
function useGetForm(spaceID, environment, entryID, locale, usePreviewAPI, bypassCache, isProduction, accessToken, accessConfig, language, preFillValuesWithCookies, setError) {
  var isFirstRender = (0, _react.useRef)(true);
  var [fieldValues, setFieldValues] = (0, _react.useState)({});
  var [fieldReferences, setFieldReferences] = (0, _react.useState)({}); // Key will be field ID and value will be React.createRef()
  var [showErrorFields, setShowErrorFields] = (0, _react.useState)(false);
  var fieldData = (0, _react.useRef)({});
  var formMetaDataRef = (0, _react.useRef)({});
  var [consentField, setConsentField] = (0, _react.useState)(undefined);
  var [formMetaData, setFormMetaData] = (0, _react.useState)({
    submitButtonLabel: _staticContent.FORM_COMPONENT.DEFAULT_SUBMIT_BUTTON[language],
    postSubmissionMessage: _staticContent.FORM_COMPONENT.THANK_YOU_MESSAGE[language],
    unsuccessfulSubmissionMessage: _staticContent.FORM_COMPONENT.UNSUCCESSFUL_MESSAGE[language],
    fields: []
  });
  (0, _react.useEffect)(() => {
    formMetaDataRef.current = formMetaData;
  }, [formMetaData]);
  var onFieldChange = (0, _react.useCallback)((field, value, isValid, callback) => {
    var {
      id
    } = field;
    if (!id) {
      _errorHandlerUtility.default.report(JSON.stringify({
        message: "Error: ".concat(id, " is not defined or empty"),
        fileName,
        methodName: 'onFieldChange'
      }));
      throw new Error("Error: ".concat(id, " is not defined or empty"));
    }

    // Avoiding triggering at form load
    // because of assignation of default form field values
    if (Array.isArray(value) && value.length > 0 || !Array.isArray(value) && !!value) {
      (0, _analyticsUtils.triggerAnalyticsFormStartEvent)(formMetaDataRef.current, fieldData);
    }
    if (isValid) {
      fieldData.current[id].isValid = true;
    } else {
      fieldData.current[id].isValid = false;
    }
    fieldData.current[id].value = value;

    // UPDATE CONDITIONAL FIELDS VISIBILITY
    // Handle conditional fields visibility updates when field values change
    if (field.isConditionalField || field.type === _constants.FIELD_TYPES.CHECKBOX_FIELD || field.type === _constants.FIELD_TYPES.RADIOBUTTON_FIELD || field.type === _constants.FIELD_TYPES.DROPDOWNLIST_FIELD) {
      // Find the field definition in formMetaData
      var findFieldInMetadata = (fieldId, fields) => {
        if (!Array.isArray(fields)) return null;
        for (var i = 0; i < fields.length; i++) {
          var currentField = fields[i];
          if (currentField.id === fieldId) {
            return currentField;
          }

          // Check in field groups
          if (isFieldGroup(currentField) && Array.isArray(currentField.fields)) {
            var found = findFieldInMetadata(fieldId, currentField.fields);
            if (found) return found;
          }

          // Check in step tracker
          if (isStepTracker(currentField) && Array.isArray(currentField.steps)) {
            for (var step of currentField.steps) {
              if (Array.isArray(step.fields)) {
                var _found = findFieldInMetadata(fieldId, step.fields);
                if (_found) return _found;
              }
            }
          }

          // Check in conditional fields
          if (Array.isArray(currentField.options)) {
            for (var option of currentField.options) {
              var _option$conditionalFi;
              if (Array.isArray(option === null || option === void 0 || (_option$conditionalFi = option.conditionalFieldGroup) === null || _option$conditionalFi === void 0 ? void 0 : _option$conditionalFi.fields)) {
                var _found2 = findFieldInMetadata(fieldId, option.conditionalFieldGroup.fields);
                if (_found2) return _found2;
              }
            }
          }
        }
        return null;
      };

      // Recursively update visibility of fields
      var updateFieldVisibility = (fields, isVisible) => {
        if (!Array.isArray(fields)) return;
        fields.forEach(nestedField => {
          if (nestedField.id && fieldData.current[nestedField.id]) {
            // Update visibility state
            fieldData.current[nestedField.id].isHidden = !isVisible;

            // If hidden, mark as valid to prevent validation errors
            if (!isVisible) {
              fieldData.current[nestedField.id].isValid = true;
            }
          }

          // Process field groups
          if (isFieldGroup(nestedField) && Array.isArray(nestedField.fields)) {
            updateFieldVisibility(nestedField.fields, isVisible);
          }

          // Process step tracker
          if (isStepTracker(nestedField) && Array.isArray(nestedField.steps)) {
            nestedField.steps.forEach(step => {
              if (Array.isArray(step.fields)) {
                updateFieldVisibility(step.fields, isVisible);
              }
            });
          }

          // Process nested conditional fields
          if ((nestedField.isConditionalField || nestedField.type === _constants.FIELD_TYPES.CHECKBOX_FIELD || nestedField.type === _constants.FIELD_TYPES.RADIOBUTTON_FIELD || nestedField.type === _constants.FIELD_TYPES.DROPDOWNLIST_FIELD) && Array.isArray(nestedField.options)) {
            nestedField.options.forEach(option => {
              var _option$conditionalFi2;
              if (Array.isArray(option === null || option === void 0 || (_option$conditionalFi2 = option.conditionalFieldGroup) === null || _option$conditionalFi2 === void 0 ? void 0 : _option$conditionalFi2.fields)) {
                var _fieldData$current$ne;
                // For nested conditionals, check if this option is selected
                var nestedValue = (_fieldData$current$ne = fieldData.current[nestedField.id]) === null || _fieldData$current$ne === void 0 ? void 0 : _fieldData$current$ne.value;
                var isNestedOptionSelected = Array.isArray(nestedValue) ? nestedValue.includes(option.value) : nestedValue === option.value;

                // Only show these fields if parent is visible AND this option is selected
                updateFieldVisibility(option.conditionalFieldGroup.fields, isVisible && isNestedOptionSelected);
              }
            });
          }
        });
      };

      // Find the current field in the form metadata
      var fieldDef = findFieldInMetadata(id, formMetaDataRef.current.fields);
      if (fieldDef && Array.isArray(fieldDef.options)) {
        // Update conditional fields visibility based on the selected option(s)
        fieldDef.options.forEach(option => {
          var _option$conditionalFi3;
          if (Array.isArray(option === null || option === void 0 || (_option$conditionalFi3 = option.conditionalFieldGroup) === null || _option$conditionalFi3 === void 0 ? void 0 : _option$conditionalFi3.fields)) {
            var isOptionSelected = field.type === _constants.FIELD_TYPES.CHECKBOX_FIELD ? Array.isArray(value) && value.includes(option.value) : value === option.value;
            updateFieldVisibility(option.conditionalFieldGroup.fields, isOptionSelected);
          }
        });
      }
    }
    setFieldValues(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [id]: value
    }));
  }, []);
  var initializeFieldData = (field, defaultFieldData) => {
    fieldData.current[field.id] = _objectSpread(_objectSpread({}, fieldData.current[field.id]), defaultFieldData);
  };
  var createFieldReference = field => {
    setFieldReferences(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [field.id]: /*#__PURE__*/(0, _react.createRef)()
    }));
  };
  var bindOnChange = field => {
    field.onChange = (value, isValid) => onFieldChange(field, value, isValid);
  };
  var processFields = (fields, defaultFieldData) => {
    fields.forEach(field => {
      if (!field || !field.id) {
        console.warn('Invalid field encountered:', field);
        return;
      }

      // Create reference and initialize data for ALL fields first (not just non-rich-text fields)
      if (field.id) {
        if (!isStepTracker(field) && !isRichTextField(field) && !isFieldGroup(field)) {
          bindOnChange(field);
          createFieldReference(field);
          initializeFieldData(field, defaultFieldData);
        }
      }

      // Then process nested fields
      if (isFieldGroup(field) && Array.isArray(field.fields)) {
        processFields(field.fields, defaultFieldData);
      } else if (isConditionalField(field) && Array.isArray(field.options)) {
        field.isConditionalField = true;
        field.options.forEach(option => {
          var _option$conditionalFi4;
          if (Array.isArray((_option$conditionalFi4 = option.conditionalFieldGroup) === null || _option$conditionalFi4 === void 0 ? void 0 : _option$conditionalFi4.fields)) {
            // Process nested conditional fields
            processFields(option.conditionalFieldGroup.fields, defaultFieldData);
          }
        });
      } else if (isStepTracker(field) && Array.isArray(field.steps)) {
        field.steps.forEach(step => {
          if (Array.isArray(step.fields)) {
            processFields(step.fields, defaultFieldData);
          }
        });
      }
    });
  };
  var isConditionalField = field => {
    var isConditionalFieldType = false;
    // Check if field type can be one of the 3 possible that can have conditional fields
    if (field.type === _constants.FIELD_TYPES.RADIOBUTTON_FIELD || field.type === _constants.FIELD_TYPES.CHECKBOX_FIELD || field.type === _constants.FIELD_TYPES.DROPDOWNLIST_FIELD) {
      // Check if the field has conditionalFieldGroup data
      if (Array.isArray(field === null || field === void 0 ? void 0 : field.options)) {
        field.options.forEach(option => {
          var _option$conditionalFi5;
          if (Array.isArray(option === null || option === void 0 || (_option$conditionalFi5 = option.conditionalFieldGroup) === null || _option$conditionalFi5 === void 0 ? void 0 : _option$conditionalFi5.fields)) {
            isConditionalFieldType = true;

            // Recursive function to process nested fields at any depth
            var processNestedFields = nestedFields => {
              nestedFields.forEach(nestedField => {
                var _fieldData$current;
                // Mark the field as hidden if it exists in fieldData
                if (nestedField.id && fieldData !== null && fieldData !== void 0 && (_fieldData$current = fieldData.current) !== null && _fieldData$current !== void 0 && _fieldData$current[nestedField.id]) {
                  fieldData.current[nestedField.id].isHidden = true;
                }

                // Handle nested fields within field groups
                if (isFieldGroup(nestedField) && Array.isArray(nestedField.fields)) {
                  processNestedFields(nestedField.fields);
                }

                // Handle nested conditional fields (recursive case)
                if (isConditionalField(nestedField) && Array.isArray(nestedField.options)) {
                  nestedField.options.forEach(nestedOption => {
                    var _nestedOption$conditi;
                    if (Array.isArray(nestedOption === null || nestedOption === void 0 || (_nestedOption$conditi = nestedOption.conditionalFieldGroup) === null || _nestedOption$conditi === void 0 ? void 0 : _nestedOption$conditi.fields)) {
                      processNestedFields(nestedOption.conditionalFieldGroup.fields);
                    }
                  });
                }

                // Handle step tracker fields
                if (isStepTracker(nestedField) && Array.isArray(nestedField.steps)) {
                  nestedField.steps.forEach(step => {
                    if (Array.isArray(step.fields)) {
                      processNestedFields(step.fields);
                    }
                  });
                }
              });
            };

            // Start the recursive processing of nested fields
            processNestedFields(option.conditionalFieldGroup.fields);
          }
        });
      }
    }
    return isConditionalFieldType;
  };
  (0, _react.useEffect)(() => {
    if (!isFirstRender.current) return;
    var defaultFieldData = {
      value: '',
      isValid: false,
      isHidden: false
    };
    var queryParams = {
      spaceID,
      environment,
      entryID,
      locale,
      usePreviewAPI,
      bypassCache,
      isProduction
    };
    (0, _utils.makeHTTPRequest)(queryParams, accessToken, isProduction, accessConfig, '', _constants.HTTP_GET).then(response => {
      if (response.status === 401) {
        var actualFormURL = "".concat(window.location.origin).concat(window.location.pathname).concat(window.location.search).concat(window.location.hash);
        var haloAuthURL = "https://www.telus.com/team-auth/sso/login?erd=".concat(encodeURIComponent(actualFormURL));
        document.location.href = haloAuthURL;
        throw new Error("Unauthorized access! Please login");
      }

      // Convert a 400-series status to an error; 500-series will be thrown automatically
      if (response.status === 400 || response.status > 401) {
        _errorHandlerUtility.default.report(JSON.stringify({
          spaceID,
          environment,
          entryID,
          usePreviewAPI,
          bypassCache,
          isProduction,
          accessConfig,
          language,
          message: "Error fetching form data! entryID: ".concat(entryID, ", status code: ").concat(response.status),
          fileName,
          methodName: 'onFieldChange'
        }));
        throw new Error("Error fetching form data! entryID: ".concat(entryID, ", status code: ").concat(response.status));
      }
      return response.json();
    }).then(data => {
      var _data$urlParametersTo;
      (0, _validations.validateAll)(data, entryID);
      if (!(0, _analyticsUtils.validateFormAnalyticsRequirements)(data)) {
        throw new Error(_constants.ANALYTICS_ERROR_MESSAGES.MISSING_CONTENTFUL_DATA);
      }
      if ((_data$urlParametersTo = data.urlParametersToVerify) !== null && _data$urlParametersTo !== void 0 && _data$urlParametersTo.length && !(0, _utils.validateURLParameters)(data.urlParametersToVerify, preFillValuesWithCookies)) {
        data.fields = data.errorFields;
        setShowErrorFields(true);
      }
      processFields(data.fields, defaultFieldData);
      if (data.consentField && Array.isArray(data.consentField) && data.consentField.length === 1) {
        setConsentField(data.consentField[0]);
      }
      var overrideValues = {
        submitButtonLabel: data.submitButtonLabel || _staticContent.FORM_COMPONENT.DEFAULT_SUBMIT_BUTTON[language],
        postSubmissionMessage: data.postSubmissionMessage || _staticContent.FORM_COMPONENT.THANK_YOU_MESSAGE[language],
        unsuccessfulSubmissionMessage: data.unsuccessfulSubmissionMessage || _staticContent.FORM_COMPONENT.UNSUCCESSFUL_MESSAGE[language]
      };
      if (document && data.enableRecaptcha) {
        var script = document.createElement('script');
        script.src = "https://www.google.com/recaptcha/api.js?render=".concat((0, _utils.getRecaptchaKey)(isProduction, data));
        script.async = true;
        document.body.appendChild(script);
      }
      setFormMetaData(prevState => _objectSpread(_objectSpread(_objectSpread({}, prevState), data), overrideValues));
    }).catch(error => {
      var errorMessage = typeof error === 'object' ? error.message : error;
      setError(errorMessage);
      _errorHandlerUtility.default.report(JSON.stringify({
        spaceID,
        environment,
        entryID,
        usePreviewAPI,
        bypassCache,
        isProduction,
        accessConfig,
        language,
        message: errorMessage,
        fileName,
        methodName: 'makeHTTPRequest'
      }));
      (0, _analyticsUtils.triggerAnalyticsErrorEvent)(formMetaDataRef.current, _constants.ANALYTICS_ERROR_TYPES.FORM_LOAD, "Error fetching form data! entryID: ".concat(entryID));
    });
    isFirstRender.current = false;
  }, [accessToken, accessConfig, bypassCache, entryID, environment, fieldReferences, formMetaData, isProduction, language, locale, onFieldChange, setError, spaceID, usePreviewAPI, preFillValuesWithCookies]);
  return {
    fieldValues,
    fieldReferences,
    consentField,
    formMetaData,
    fieldData,
    showErrorFields
  };
}
var _default = exports.default = useGetForm;